import { useState } from 'react';
import { CardElement } from '@stripe/react-stripe-js';
import { Button, Checkbox } from '@mantine/core';
import { useTranslation } from '../../context/TranslationContext';
import paymentMethodsService from '../../services/payment_methods';
import { loadStripe } from '@stripe/stripe-js';

export const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const CARD_ELEMENT_OPTIONS = {
  hidePostalCode: true,
  style: {
    base: { fontSize: '16px', color: '#1a1a1a', '::placeholder': { color: '#aab7c4' } },
    invalid: { color: '#fa755a' },
  },
};

export default function AddCardForm({ stripe, elements, onCardAdded, onCancel }) {
  const { t } = useTranslation();
  const [errorMsg, setErrorMsg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveForFuture, setSaveForFuture] = useState(true);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setSaving(true);
    setErrorMsg(null);

    try {
      if (saveForFuture) {
        // Save card via SetupIntent, then return paymentMethodId
        const { client_secret } = await paymentMethodsService.createSetupIntent();
        const result = await stripe.confirmCardSetup(client_secret, {
          payment_method: { card: elements.getElement(CardElement) }
        });
        if (result.error) {
          setErrorMsg(result.error.message);
          return;
        }
        onCardAdded({ paymentMethodId: result.setupIntent.payment_method, isOneTime: false });
      } else {
        // One-time: card element stays mounted in parent, just signal success
        onCardAdded({ isOneTime: true });
      }
    } catch (err) {
      setErrorMsg(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="add-card-form">
      <div className="card-element-wrapper">
        <CardElement options={CARD_ELEMENT_OPTIONS} />
      </div>

      <Checkbox
        checked={saveForFuture}
        onChange={e => setSaveForFuture(e.currentTarget.checked)}
        label={t('saveCardForFuture', 'Save this card for future purchases')}
        style={{ marginTop: 8 }}
      />

      {errorMsg && <div role="alert" style={{ color: 'red', marginTop: 8, fontSize: '0.9em' }}>{errorMsg}</div>}

      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <Button type="submit" disabled={!stripe || saving} loading={saving}>
          {saving ? t('addingCard', 'Adding...') : t('addCard', 'Add Card')}
        </Button>
        <Button variant="subtle" color="gray" type="button" onClick={onCancel}>
          {t('cancel', 'Cancel')}
        </Button>
      </div>
    </form>
  );
}
